import { useEffect, useState } from "react";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { BrushStart, ChartType } from "../../../models/chart_model";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { getHaryanaDailyForecast } from "../../../Rest_api/RestApi";
import { DynamicDatePicker } from "../../../components/DatePicker";
import Loading from "../../../components/Loading";

const chartSuffixes = [
  { suffix: "demand", displayName: "Demand", stackSuffix: "demand" },
  { suffix: "average", displayName: "Average", stackSuffix: "average" },
];

interface ChartTypeOption {
  value: ChartType;
  label: string;
  stackId?: string;
}

const selectedChartType: ChartTypeOption[] = [
  {
    value: ChartType.Bar,
    label: "Standard Bar",
    stackId: undefined,
  },
  {
    value: ChartType.Line,
    label: "Line",
    stackId: undefined,
  },
];

const DailyForecast = () => {
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<string[]>([]);
  const [selectedSuffix, setSelectedSuffix] = useState([
    chartSuffixes[0],
    chartSuffixes[1],
  ]);

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [dates, setDate] = useState({
    startDate: new Date("2025-09-01"),
    endDate: new Date("2025-09-30"),
    key: "selection",
  });

  useEffect(() => {
    async function _handlefetchData() {
      setLoading(true);
      const newData = await getHaryanaDailyForecast(
        dates.startDate.toISOString().split("T")[0],
        dates.endDate.toISOString().split("T")[0]
      );

      setData(newData);
      setLoading(false);
    }

    _handlefetchData();
  }, [dates]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    ExchangeColors: string[]
  ) => {
    return {
      dataKey: `${key}_${suffix}_${"MW".toLowerCase()}`,
      name: displayName === "Demand" ? "Peak" : displayName,
      legendColor: undefined,
      stroke: displayName === "Demand" ? "#34656D" : ExchangeColors[10],
      type:
        displayName === "Demand"
          ? selectedChartType[0].value
          : selectedChartType[1].value,
      stackId:
        selectedChartType[index].stackId !== undefined
          ? stackSuffix
          : undefined,
    };
  };

  useEffect(() => {
    let filteredData = data;

    if (dates) {
      filteredData = data.filter((item) => {
        const itemDate = new Date(item.date);
        return itemDate >= dates.startDate && itemDate <= dates.endDate;
      });
    }

    // Process data to add deficit and surplus calculations

    const par: ChartDataParams = {
      keys: ["date"],
      groupBy: [],
      volumeKey: ``,
      aggregationKeys: ["demand_mw", "average_mw"],
      priceKey: "",
      data: filteredData,
    };
    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [data, dates]);

  return (
    <>
      {loading && <Loading />}
      <div className="dash-card col-span-1 md:col-span-6  h-full w-full">
        <div className="flex justify-between items-center mb-2 ">
          <h2 className="chartHeading">Daily Demand Forecast</h2>
          <DynamicDatePicker
            setDate={(date) => setDate(date[0]!)!}
            defaultDates={dates}
          />
        </div>

        <ReMixChart
          data={chartData}
          showBrush={chartData.length > 96 * 7 ? true : undefined}
          brushStart={BrushStart.Start}
          brushIndex={
            chartData.length > 96 * 7
              ? { startIndex: 0, endIndex: 96 * 7 }
              : undefined
          }
          isTimeSlot={false}
          onlyTitle={true}
          barCategoryGap={10}
          unit={"MW"}
          yAxisLabel={"MW"}
          legends={selectedSuffix.flatMap(
            ({ suffix, displayName, stackSuffix }) =>
              chartKeys.map(
                (key, index) =>
                  createChartObject(
                    key,
                    index,
                    suffix,
                    displayName,
                    stackSuffix,
                    ExchangeColors
                  ) as any
              )
          )}
          xDataKey={"date"}
          xTick={undefined}
          secondXDataKey={undefined}
        />
      </div>
    </>
  );
};

export default DailyForecast;
